/**
 * MasterWEB Market — policy renderer R8.10
 *
 * Robust presentation layer for Shopify native policy pages.
 * The legal copy remains managed by Shopify Admin. This module only enhances
 * the public storefront DOM: it removes legacy leaked CSS, normalizes visual
 * inline styles, identifies sections even when Shopify Rich Text serializes
 * them inside ordered lists, replaces fragile/manual tables of contents with a
 * deterministic accessible navigation and neutralizes orphan list markers.
 */
(() => {
  'use strict';

  const BODY_SELECTOR = '.shopify-policy__body';
  const MASTERWEB_CSS_MARKERS = [
    '.mw-shipping-policy{', '.mw-shipping-policy {',
    '.mw-return-policy{', '.mw-return-policy {',
    '.mw-privacy-policy{', '.mw-privacy-policy {',
    '.mw-terms-policy{', '.mw-terms-policy {'
  ];
  const CONTENT_MARKERS = [
    'MASTERWEB MARKET', 'MasterWEB Market', 'Shipping Policy',
    'Return & Refund Policy', 'Privacy Policy', 'Terms & Conditions'
  ];

  const policyI18n = (() => {
    const node = document.getElementById('MwPolicyI18n');
    try { return node ? JSON.parse(node.textContent || '{}') : {}; } catch (_) { return {}; }
  })();
  const normalizedTocLabel = String(policyI18n.toc || 'Policy contents').replace(/\s+/g, ' ').trim().toLowerCase();
  const LEGACY_TOC_LABELS = new Set(['policy contents', 'table of contents']);
  const DATE_RE = /^last\s+updated\s*:/i;
  const BRAND_RE = /^masterweb\s+market$/i;
  const NUMBERED_RE = /^(\d{1,2})(?:[.)])?\s+(.+)$/;
  const STANDALONE_NUMBER_RE = /^\d{1,2}[.)]?$/;

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const isTocLabel = (value) => {
    const text = normalize(value).toLowerCase();
    return text === normalizedTocLabel || LEGACY_TOC_LABELS.has(text);
  };
  const directElements = (root) => Array.from(root.children || []).filter((el) => normalize(el.textContent));

  function contentRoot(body) {
    return Array.from(body.children).find((el) => el.classList?.contains('rte')) || body;
  }

  function looksLikeMasterwebCss(text) {
    const compact = normalize(text);
    if (!compact) return false;
    if (MASTERWEB_CSS_MARKERS.some((marker) => compact.startsWith(marker))) return true;
    return compact.includes('--mw-text:') && compact.includes('box-sizing') && compact.includes('.mw-');
  }

  function firstContentMarkerIndex(text) {
    let best = -1;
    for (const marker of CONTENT_MARKERS) {
      const index = text.indexOf(marker);
      if (index > 0 && (best === -1 || index < best)) best = index;
    }
    return best;
  }

  function cleanLeadingCssPayload(root) {
    let cleaned = false;
    for (const node of Array.from(root.childNodes)) {
      const text = normalize(node.textContent);
      if (!text) continue;
      if (!looksLikeMasterwebCss(text)) break;

      const markerIndex = firstContentMarkerIndex(text);
      if (markerIndex > 0) {
        const remainder = text.slice(markerIndex).trim();
        if (node.nodeType === Node.TEXT_NODE) node.nodeValue = remainder;
        else node.textContent = remainder;
      } else {
        node.remove();
      }
      cleaned = true;
    }

    while (root.firstElementChild) {
      const first = root.firstElementChild;
      const useful = first.querySelector?.('img,video,iframe,table,ul,ol,a');
      if (normalize(first.textContent) || useful) break;
      first.remove();
    }
    if (cleaned) root.dataset.mwPolicyCssCleaned = 'true';
  }

  function policyTypeFromPath() {
    const path = window.location.pathname.toLowerCase();
    if (path.includes('shipping-policy')) return 'shipping';
    if (path.includes('refund-policy')) return 'refund';
    if (path.includes('privacy-policy')) return 'privacy';
    if (path.includes('terms-of-service') || path.includes('subscription-policy')) return 'terms';
    return 'generic';
  }

  function slugify(value) {
    return normalize(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 72);
  }

  function normalizePresentation(root) {
    const presentationProps = new Set([
      'color', 'background', 'background-color', 'font-family', 'font-size',
      'font-weight', 'line-height', 'letter-spacing', 'margin', 'margin-top',
      'margin-right', 'margin-bottom', 'margin-left', 'padding', 'padding-top',
      'padding-right', 'padding-bottom', 'padding-left', 'text-align',
      'text-decoration', 'border', 'border-color', 'border-width', 'border-style'
    ]);

    for (const element of root.querySelectorAll('[style]')) {
      for (const prop of Array.from(element.style)) {
        if (presentationProps.has(prop)) element.style.removeProperty(prop);
      }
      if (!element.getAttribute('style')?.trim()) element.removeAttribute('style');
      element.dataset.mwPolicyNormalized = 'true';
    }
  }

  function findInternalTitle(root) {
    const candidates = Array.from(root.querySelectorAll('h1,h2,h3,p,div')).slice(0, 24);
    return candidates.find((el) => {
      const text = normalize(el.textContent);
      if (!text || text.length > 110 || BRAND_RE.test(text) || DATE_RE.test(text) || isTocLabel(text)) return false;
      if (el.closest('.mw-policy-native-nav')) return false;
      if (el.matches('h1,h2,h3')) return /policy|terms|conditions|privacy|refund|return|shipping/i.test(text);
      return /^policy\b/i.test(text) && text.length <= 90;
    });
  }

  function decorateIntro(root, container) {
    const blocks = directElements(root).slice(0, 16);
    for (const element of blocks) {
      const text = normalize(element.textContent);
      if (BRAND_RE.test(text)) element.classList.add('mw-policy-brandline');
      if (DATE_RE.test(text)) element.classList.add('mw-policy-date');
    }

    const title = findInternalTitle(root);
    if (title) {
      title.classList.add('mw-policy-internal-title');
      container?.classList.add('mw-policy-has-internal-title');
    }

    const titleIndex = title ? blocks.indexOf(title) : -1;
    const intro = blocks.find((element, index) => {
      if (index <= titleIndex) return false;
      if (element.matches('h1,h2,h3,h4,ul,ol,table,blockquote,nav')) return false;
      if (element.classList.contains('mw-policy-date') || element.classList.contains('mw-policy-brandline')) return false;
      const text = normalize(element.textContent);
      return text.length >= 50 && text.length <= 900 && !isTocLabel(text);
    });
    if (intro) intro.classList.add('mw-policy-intro');
  }

  function isExcludedHeading(el) {
    const text = normalize(el.textContent);
    return !text || el.classList.contains('mw-policy-internal-title') || isTocLabel(text) || el.closest('.mw-policy-native-nav');
  }

  function sectionListFirstBlocks(root) {
    const candidates = [];
    for (const list of root.querySelectorAll('ol,ul')) {
      const items = Array.from(list.children).filter((el) => el.matches('li'));
      if (items.length < 2) continue;

      const looksLikeToc = items.every((li) => {
        const links = li.querySelectorAll('a').length;
        const text = normalize(li.textContent);
        return links >= 1 && text.length <= 160 && li.children.length <= 2;
      });
      if (looksLikeToc) continue;

      const blocks = [];
      for (const li of items) {
        const first = Array.from(li.children).find((el) => normalize(el.textContent));
        const liText = normalize(li.textContent);
        if (!first || normalize(first.textContent).length > 130) continue;
        if (li.children.length >= 2 || liText.length > 180) blocks.push(first);
      }
      if (blocks.length >= 2) candidates.push(...blocks);
    }
    return candidates;
  }

  function sectionCandidates(root) {
    // Prefer one semantic level only. This prevents h3 subheadings such as
    // "Tempo di elaborazione" from becoming top-level TOC entries when the
    // policy already has real h2 sections.
    const h2 = Array.from(root.querySelectorAll('h2')).filter((el) => !isExcludedHeading(el));
    if (h2.length >= 2) return h2;

    const h3 = Array.from(root.querySelectorAll('h3')).filter((el) => !isExcludedHeading(el));
    if (h3.length >= 2) return h3;

    const listBlocks = sectionListFirstBlocks(root).filter((el) => !isExcludedHeading(el));
    if (listBlocks.length >= 2) return listBlocks;

    const pseudo = Array.from(root.querySelectorAll('p,div')).filter((el) => {
      if (el.closest('.mw-policy-native-nav')) return false;
      const text = normalize(el.textContent);
      return text.length <= 130 && NUMBERED_RE.test(text) && el.querySelectorAll('p,div,section,article').length === 0;
    });
    return pseudo.length >= 2 ? pseudo : [...h2, ...h3];
  }

  function firstTextNode(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (normalize(node.nodeValue)) return node;
    }
    return null;
  }

  function removeNumberPrefixFromHeading(heading) {
    const full = normalize(heading.textContent);
    const match = full.match(NUMBERED_RE);
    if (!match) return { number: null, label: full };

    const textNode = firstTextNode(heading);
    if (textNode) {
      const original = textNode.nodeValue || '';
      const replaced = original.replace(/^\s*\d{1,2}(?:[.)])?\s+/, '');
      if (replaced !== original) textNode.nodeValue = replaced;
    }
    return { number: match[1], label: match[2] };
  }

  function previousStandaloneNumber(heading) {
    let prev = heading.previousElementSibling;
    if (!prev) return null;
    const text = normalize(prev.textContent);
    if (!STANDALONE_NUMBER_RE.test(text) || prev.children.length) return null;
    return { element: prev, number: text.replace(/\D/g, '') };
  }

  function sectionNumberFromList(heading) {
    const li = heading.closest('li');
    if (!li) return null;
    const list = li.parentElement;
    if (!list?.matches('ol,ul')) return null;
    const items = Array.from(list.children).filter((el) => el.matches('li'));
    const index = items.indexOf(li);
    return index >= 0 ? String(index + 1) : null;
  }

  function decorateSections(root, headings) {
    const listGroups = new Map();

    headings.forEach((heading, index) => {
      heading.classList.add('mw-policy-section-title');
      heading.setAttribute('role', 'heading');
      heading.setAttribute('aria-level', heading.matches('h2') ? '2' : '2');

      const parsed = removeNumberPrefixFromHeading(heading);
      const standalone = previousStandaloneNumber(heading);
      let number = parsed.number || standalone?.number || sectionNumberFromList(heading) || String(index + 1);

      if (standalone) {
        standalone.element.classList.add('mw-policy-orphan-number');
        standalone.element.setAttribute('aria-hidden', 'true');
      }

      heading.dataset.mwSectionNumber = number;
      heading.dataset.mwSectionLabel = normalize(heading.textContent) || parsed.label;

      const li = heading.closest('li');
      const list = li?.parentElement;
      if (li && list?.matches('ol,ul')) {
        if (!listGroups.has(list)) listGroups.set(list, []);
        listGroups.get(list).push({ li, heading });
      }
    });

    for (const [list, items] of listGroups) {
      if (items.length < 2) continue;
      list.classList.add('mw-policy-section-list');
      for (const { li } of items) li.classList.add('mw-policy-section-item');
    }
  }

  function ensureUniqueId(element, index) {
    if (element.id) return element.id;
    const label = element.dataset.mwSectionLabel || element.textContent;
    const base = `mw-policy-${slugify(label) || `section-${index + 1}`}`;
    let id = base;
    let suffix = 2;
    while (document.getElementById(id)) id = `${base}-${suffix++}`;
    element.id = id;
    return id;
  }

  function topLevelBlock(root, element) {
    let current = element;
    while (current?.parentElement && current.parentElement !== root) current = current.parentElement;
    return current || element;
  }

  function isBefore(a, b) {
    return Boolean(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
  }

  function tocTitleCandidates(root) {
    const matches = Array.from(root.querySelectorAll('p,h2,h3,h4,div,strong')).filter((el) => {
      if (el.closest('.mw-policy-native-nav')) return false;
      const text = normalize(el.textContent);
      return isTocLabel(text) || (text.toLowerCase().startsWith(normalizedTocLabel) && el.querySelectorAll('a').length >= 2);
    });

    // Shopify can wrap the label in <strong> inside a paragraph. Work with the
    // visual block instead of hiding only the inner inline element.
    return matches.map((el) => {
      const parent = el.parentElement;
      if (el.matches('strong') && parent && parent !== root && normalize(parent.textContent) === normalize(el.textContent)) return parent;
      return el;
    }).filter((el, index, array) => array.indexOf(el) === index);
  }

  function hideSourceToc(root, firstHeading) {
    if (!firstHeading) return;
    const firstBlock = topLevelBlock(root, firstHeading);
    const titles = tocTitleCandidates(root).filter((el) => isBefore(el, firstHeading));
    if (!titles.length) return;

    // Prefer the closest TOC marker before the first real section.
    const title = titles[titles.length - 1];
    if (title.contains(firstHeading) || firstHeading.contains(title)) return;
    title.classList.add('mw-policy-source-toc');
    title.setAttribute('aria-hidden', 'true');

    // A container that already includes several internal links is itself the TOC.
    if (title.querySelectorAll('a[href*="#"]').length >= 2 && title !== root) return;

    let cursor = title.nextElementSibling;
    let guard = 0;
    while (cursor && cursor !== firstBlock && !cursor.contains(firstHeading) && guard++ < 40) {
      const text = normalize(cursor.textContent);
      const internalLinks = cursor.querySelectorAll('a[href*="#"]').length;
      const allLinks = cursor.querySelectorAll('a').length;
      const isList = cursor.matches('ol,ul');
      const isCompact = text.length <= 2600;
      const numberedLines = (text.match(/(?:^|\s)\d{1,2}[.)]?\s+\S/g) || []).length;
      const tocLike = isCompact && (isList || internalLinks >= 1 || (allLinks >= 2 && text.length < 2200) || numberedLines >= 4);

      if (!text) {
        cursor = cursor.nextElementSibling;
        continue;
      }
      if (!tocLike) break;

      cursor.classList.add('mw-policy-source-toc');
      cursor.setAttribute('aria-hidden', 'true');
      cursor = cursor.nextElementSibling;
    }
  }

  function buildNavigation(root, headings) {
    if (headings.length < 3 || root.querySelector(':scope > .mw-policy-native-nav')) return;

    const nav = document.createElement('nav');
    nav.className = 'mw-policy-native-nav';
    nav.setAttribute('aria-label', policyI18n.toc || 'Policy contents');

    const title = document.createElement('p');
    title.className = 'mw-policy-native-nav__title';
    title.textContent = policyI18n.toc || 'Policy contents';

    const list = document.createElement('ol');
    list.className = 'mw-policy-native-nav__grid';

    headings.forEach((heading, index) => {
      const label = normalize(heading.dataset.mwSectionLabel || heading.textContent);
      if (!label) return;
      const id = ensureUniqueId(heading, index);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${id}`;
      a.textContent = `${heading.dataset.mwSectionNumber || index + 1}. ${label}`;
      li.appendChild(a);
      list.appendChild(li);
    });

    nav.append(title, list);
    const insertionPoint = topLevelBlock(root, headings[0]);
    root.insertBefore(nav, insertionPoint);
  }

  function decorateCallouts(root) {
    const candidates = root.querySelectorAll('p,h3,h4,strong,div');
    for (const el of candidates) {
      if (el.closest('.mw-policy-native-nav')) continue;
      const text = normalize(el.textContent);
      if (!text || text.length > 300) continue;
      if (/^free standard shipping in the united states\.?$/i.test(text)) el.classList.add('mw-policy-success');
      if (/^unexpected payment requests$/i.test(text)) el.classList.add('mw-policy-warning');
      if (/^possible delivery time variations$/i.test(text)) el.classList.add('mw-policy-info');
      if (/^customer support$/i.test(text)) {
        const next = el.nextElementSibling;
        if (next && normalize(next.textContent)) next.classList.add('mw-policy-contact-card');
      }
    }
  }

  function improveExternalLinks(root) {
    for (const link of root.querySelectorAll('a[target="_blank"]')) {
      const rel = new Set(normalize(link.getAttribute('rel')).split(' ').filter(Boolean));
      rel.add('noopener');
      rel.add('noreferrer');
      link.setAttribute('rel', Array.from(rel).join(' '));
    }
  }

  function init() {
    const body = document.querySelector(BODY_SELECTOR);
    if (!body || body.dataset.mwPolicyEnhanced === 'true') return;

    const container = body.closest('.shopify-policy__container');
    const root = contentRoot(body);

    cleanLeadingCssPayload(root);
    normalizePresentation(root);
    body.dataset.mwPolicyType = policyTypeFromPath();
    decorateIntro(root, container);

    const headings = sectionCandidates(root);
    decorateSections(root, headings);
    headings.forEach((heading, index) => ensureUniqueId(heading, index));
    hideSourceToc(root, headings[0]);
    buildNavigation(root, headings);
    decorateCallouts(root);
    improveExternalLinks(root);

    body.dataset.mwPolicyEnhanced = 'true';
    container?.classList.add('mw-policy-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
